import { OptionProperties, UpdateBehaviorTreatmentProperties } from '../../../redux/API/ClientAPIHelpers/soapNoteProperties';
import { ERRORS } from './behaviorTreatmentReducer';

// Mirrors the server-side submit-time rules in BehaviorTreatmentReportModelValidator.cs, so the
// BT can see what's missing before actually submitting. Deterministic, no AI — same as the backend.
export interface SubmitValidationIssue {
	key: keyof typeof ERRORS;
	label: string;
	message: string;
}

const isOtherSelected = (ids: string[] | undefined, list: OptionProperties[]): boolean =>
	Boolean(ids?.some((id) => list.find((o) => String(o.id) === id)?.text === 'Other'));

export const validateBehaviorTreatmentForSubmit = (
	data: UpdateBehaviorTreatmentProperties,
	lists: {
		barriersList: OptionProperties[];
		behaviorTreatmentFutureList: OptionProperties[];
		clientMoodList: OptionProperties[];
		participantsList: OptionProperties[];
		reinforcementList: OptionProperties[];
	},
	isDoeFunded = false,
): SubmitValidationIssue[] => {
	const issues: SubmitValidationIssue[] = [];
	const push = (key: keyof typeof ERRORS, label: string, message: string) => issues.push({ key, label, message });

	if (!data.locationId) push('LocationId', 'Location', 'Location is required.');

	if (!data.sessionDescription?.trim()) {
		push('SessionDescription', 'Session description', 'Session description is required.');
	}

	if (!data.barrierToTreatmentResponses?.length) {
		push('BarrierToTreatmentResponses', 'Barriers to treatment', "Please provide at least one 'Barriers to treatment'.");
	}

	if (isOtherSelected(data.barrierToTreatmentResponses, lists.barriersList) && !data.barrierToTreatmentOther?.trim()) {
		push('BarrierToTreatmentOther', 'Other barrier details', 'Please provide details about other barriers to treatment.');
	}

	if (!data.howBarrierAffect?.trim()) {
		push('HowBarrierAffect', 'Barrier impact', "Please provide a response for 'How did the above barrier affect the session?'.");
	}

	if (!data.skillDomainAddressedResponses?.length) {
		push('SkillDomainAddressedResponses', 'Skill domains addressed', "Please provide at least one 'Skill Domains Addressed'.");
	}

	if (!data.behaviorTreatmentTechniquesResponses?.length) {
		push('BehaviorTreatmentTechniquesResponses', 'Techniques used', "Please provide at least one 'Techniques Used'.");
	}

	if (!data.workedGoals?.trim()) {
		const goalCount = isDoeFunded ? 2 : 3;
		push('WorkedGoals', 'Goals addressed', `Please provide a response for 'Describe ${goalCount} goals that were addressed in the session'.`);
	}

	if (data.levelOfMaladaptiveBehavior == null) {
		push('LevelOfMaladaptiveBehavior', 'Level of maladaptive behavior', 'Level of maladaptive behavior is required.');
	}

	if (!data.maladaptiveBehaviorTechniquesResponses?.length) {
		push('MaladaptiveBehaviorTechniquesResponses', 'Maladaptive behavior techniques', "Please provide at least one 'Maladaptive behaviors'.");
	}

	if (!data.frequencyAndDurationOfMaladaptiveBehaviors?.trim()) {
		push(
			'FrequencyAndDurationOfMaladaptiveBehaviors',
			'Frequency & duration of maladaptive behavior',
			"Please provide a response for 'Describe the frequency and duration of the maladaptive behavior and the techniques used as an intervention'.",
		);
	}

	if (!data.clientMoodResponses?.length) {
		push('ClientMoodResponses', 'Client mood', "Please provide at least one 'Client moods'.");
	}

	if (isOtherSelected(data.clientMoodResponses, lists.clientMoodList) && !data.clientMoodOther?.trim()) {
		push('ClientMoodOther', 'Other client mood', 'Please provide details about other client mood.');
	}

	if (data.clientResponseToTreatment == null) {
		push('ClientResponseToTreatment', 'Client response to treatment', 'Client response to treatment is required.');
	}

	if (!data.behaviorTreatmentFuturePlanResponses?.length) {
		push('BehaviorTreatmentFuturePlanResponses', 'Plan for future session', "Please provide at least one 'Future plans'.");
	}

	if (isOtherSelected(data.behaviorTreatmentFuturePlanResponses, lists.behaviorTreatmentFutureList) && !data.behaviorTreatmentFuturePlanOther?.trim()) {
		push('FuturePlanOther', 'Other future plan', 'Please provide details about other future plan.');
	}

	if (!data.reinforcementResponses?.length) {
		push('Reinforcement', 'Reinforcers used', "Please provide at least one 'Reinforcements'.");
	}

	if (isOtherSelected(data.reinforcementResponses, lists.reinforcementList) && !data.reinforcementOther?.trim()) {
		push('ReinforcementOther', 'Other reinforcer', 'Please provide details about other reinforcement.');
	}

	if (isOtherSelected(data.participantResponses, lists.participantsList) && !data.behaviorTreatmentParticipantOther?.trim()) {
		push('BehaviorTreatmentParticipantOther', 'Other participant details', 'Please specify the participant details.');
	}

	if (!data.signature?.trim()) push('Signature', 'Signature', 'Signature is required.');

	return issues;
};
